import React, { useId } from 'react';
import classNames from 'classnames';
import styles from './FlowAiOrb.module.scss';

export type FlowAiOrbState = 'ready' | 'listening' | 'thinking' | 'speaking';

export interface FlowAiOrbProps {
  size: number;
  state?: FlowAiOrbState;
  className?: string;
}

const STATE_CLASS: Record<FlowAiOrbState, string | undefined> = {
  ready: undefined,
  listening: styles.listening,
  thinking: styles.thinking,
  speaking: styles.speaking,
};

const FlowAiOrb: React.FC<FlowAiOrbProps> = ({ size, state = 'ready', className }) => {
  const id = useId();
  const gradientE = `${id}-e`;
  const gradientW = `${id}-w`;

  return (
    <span
      className={classNames(styles.orb, size <= 64 && styles.small, STATE_CLASS[state], className)}
      style={{ width: size, height: size }}
      aria-hidden>
      <span className={styles.outerRing} />
      <span className={styles.ring} />
      <span className={styles.dot} />
      <span className={classNames(styles.dot, styles.dotSmall)} />
      <span className={styles.disc}>
        <svg className={styles.mark} viewBox="-1 5 37 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient
              id={gradientE}
              x1="20.1857"
              y1="18.5767"
              x2="30.0097"
              y2="21.4609"
              gradientUnits="userSpaceOnUse">
              <stop stopColor="#2F3A8B" />
              <stop offset="0.0515" stopColor="#2F3A8B" />
              <stop offset="0.3573" stopColor="#36A9E1" />
              <stop offset="0.6827" stopColor="#66C5DB" />
              <stop offset="1" stopColor="#66C5DB" />
            </linearGradient>
            <linearGradient
              id={gradientW}
              x1="0.0234"
              y1="23.1845"
              x2="24.7554"
              y2="23.1845"
              gradientUnits="userSpaceOnUse">
              <stop stopColor="#36A9E1" />
              <stop offset="0.3085" stopColor="#3EADE1" />
              <stop offset="0.6873" stopColor="#52B8DF" />
              <stop offset="1" stopColor="#66C5DB" />
            </linearGradient>
          </defs>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M24.8436 6.96961C22.1723 7.58965 20.2133 9.27264 19.2338 11.5757C19.1448 11.8414 19.0557 12.0186 18.9667 12.2843C18.8777 12.4614 18.8777 12.7272 18.7886 12.9043C18.6996 13.1701 18.6996 13.4358 18.6105 13.613C18.5215 14.0559 18.5215 14.4987 18.5215 15.0302C18.5215 17.3332 19.2338 19.282 20.5695 20.6992L20.6585 20.7878C22.1723 22.4708 24.4874 23.4451 27.0697 23.4451C28.9397 23.4451 30.8096 23.0022 32.3233 22.1165C32.8576 21.8507 33.2138 21.3193 33.2138 20.6992C33.2138 19.8134 32.4124 19.0162 31.5219 19.0162C31.2548 19.0162 30.9877 19.1048 30.7205 19.1934C29.7411 19.7249 28.4944 20.0792 27.5149 20.0792C24.7546 20.0792 23.1518 18.3962 22.8847 16.3589H32.2343C33.8371 16.3589 34.7275 15.7388 34.7275 14.4102C34.7275 10.247 31.5219 6.70387 26.7136 6.70387C26.0902 6.79245 25.4669 6.79245 24.8436 6.96961ZM22.9737 13.1701C22.9737 12.9043 23.0627 12.7272 23.1518 12.55C23.686 11.3099 24.7546 10.1584 26.8026 10.1584C29.563 10.1584 30.6315 12.1957 30.7205 13.7015H22.8847C22.8847 13.4358 22.8847 13.2587 22.9737 13.1701Z"
            fill={`url(#${gradientE})`}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M9.70582 8.56428L6.67832 17.7764L4.00699 8.38712C3.73986 7.50134 3.0275 6.8813 2.04802 6.8813C0.890442 6.8813 0 7.76708 0 8.91859C0 9.09575 0 9.36148 0.0890442 9.53864L3.65081 21.1424C4.00699 22.3825 5.16456 23.1797 6.50023 23.1797C7.83589 23.1797 8.90442 22.2939 9.2606 21.1424L12.11 12.196L14.8704 21.1424C15.2266 22.3825 16.3841 23.1797 17.7198 23.1797C19.0555 23.1797 20.124 22.2939 20.4802 21.1424L20.5692 20.6995V20.6109L22.8844 12.9932L23.0625 12.3731L24.7543 6.8813C22.172 7.50134 20.213 9.18433 19.2335 11.4874C19.1445 11.7531 19.0555 11.9302 18.9664 12.196L18.7883 12.816L18.6102 13.5247L17.4527 17.6869L14.4252 8.4757C14.158 7.50134 13.1785 6.79272 12.11 6.79272C10.9524 6.8813 9.97295 7.58992 9.70582 8.56428Z"
            fill={`url(#${gradientW})`}
          />
        </svg>
      </span>
    </span>
  );
};

export default FlowAiOrb;
